import { gql } from '@apollo/client';
import { useAuthenticator } from '@aws-amplify/ui-react';
import { Auth } from 'aws-amplify';
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { LOGIN_PATH } from 'src/common/constants';
import images from 'src/common/images';
import { MutationSet } from 'src/common/interfaces';
import { RecordState } from 'src/common/string-enum';
import { ErrorMessage, InfoMessage, InfoWord } from 'src/enum/messsage-enum';
import { deleteMstRemarks, insertMstRemarks, updateMstRemarks } from 'src/graphql/mutations';
import { getMstRemarks } from 'src/graphql/queries';
import { useAppLazyQuery, useAppMutation } from 'src/libs/hooks';
import utility from 'src/libs/utility';
import MainLayout from '../main-layout';

interface ErrorMstRemarks {
  isErrorRemarksCd: boolean;
  isErrorRemarks: boolean;
}

interface MstRemarks {
  mrm_remarks_cd: string;
  mrm_remarks_kb: number | null;
  mrm_remarks: string;
  isCannotEditRemarksCd: boolean;
  recordState?: RecordState;
  error: ErrorMstRemarks;
  isBeginData: boolean;
}

const defaultErrorMstRemarks = {
  isErrorRemarksCd: false,
  isErrorRemarks: false,
};

const emptyRemarks = {
  mrm_remarks_cd: '',
  mrm_remarks_kb: null,
  mrm_remarks: '',
  isCannotEditRemarksCd: false,
  recordState: RecordState.INSERT,
  error: defaultErrorMstRemarks,
  isBeginData: false,
};

const RemarksManage = () => {
  const header = ['コード', '区分', '内容', '削除'];
  const [mstRemarks, setMstRemarks] = useState<MstRemarks[]>([]);
  const { confirmAlert, showAlertMessage, registerData } = utility;
  const [removedMstRemarks, setRemovedMstRemarks] = useState<MstRemarks[]>([]);
  const [beginLengthListRemarks, setBeginLengthListRemarks] = useState<number>(0);
  const location = useLocation();
  const navigate = useNavigate();
  const { signOut } = useAuthenticator((context) => [context.user]);
  const [updateFunc] = useAppMutation({ query: gql(updateMstRemarks) });
  const [deleteFunc] = useAppMutation({ query: gql(deleteMstRemarks) });
  const [insertFunc] = useAppMutation({ query: gql(insertMstRemarks) });
  const [searchMstRemarks, { data: mstRemarksData, loading: isMstRemarksLoading }] = useAppLazyQuery({
    query: gql(getMstRemarks),
    onCompleted: () => {
      setRemovedMstRemarks([]);
    },
  });

  const renderTableHeader = useMemo(() => {
    return header.map((key, index) => (
      <th
        className="p-[8px] border-[1px] border-solid border-[#fff] pt-[12px] pb-[12px] text-center bg-[#a8d08d] text-[#fff]"
        key={index}
      >
        {key.toUpperCase()}
      </th>
    ));
  }, []);

  const renderTableData = useMemo(() => {
    if (!mstRemarks || mstRemarks.length <= 0) return;
    const styleInput = (isError: boolean | undefined) => {
      return `${
        isError ? 'border-[#FF0000] border-[2px] rounded-md' : 'border-{transparent}'
      } pl-[5px] bg-transparent w-full`;
    };
    return mstRemarks.map((remarks: MstRemarks, index: number) => {
      return (
        <tr className="hover:opacity-80 odd:bg-[#e2efd9]" key={index}>
          <td className="p-[8px] border-[1px] border-solid border-[#fff]">
            <input
              className={`${styleInput(remarks.error.isErrorRemarksCd)} text-right`}
              onChange={(event) => {
                if (event.target.value.length > 8) return;
                handleChangeRemarks(event.target.value, index, 'mrm_remarks_cd');
              }}
              type="number"
              name="mrm_remarks_cd"
              min={1}
              max={99999999}
              value={remarks.mrm_remarks_cd}
              disabled={remarks.isCannotEditRemarksCd}
            />
          </td>
          <td className="p-[8px] border-[1px] border-solid border-[#fff]">
            <select
              className={styleInput(false)}
              name="discountType"
              onChange={(event) => handleChangeRemarks(event.target.value, index, 'mrm_remarks_kb')}
              value={remarks.mrm_remarks_kb ?? ''}
            >
              <option value="1">支払条件</option>
              <option value="2">有効期限</option>
              <option value="3">納入予定日</option>
              <option value="4">保守契約条件</option>
            </select>
          </td>
          <td className="p-[8px] border-[1px] border-solid border-[#fff]">
            <input
              className={styleInput(remarks.error.isErrorRemarks)}
              onChange={(event) => handleChangeRemarks(event.target.value, index, 'mrm_remarks')}
              type="text"
              name="name"
              maxLength={50}
              value={remarks.mrm_remarks}
            />
          </td>
          <td className="p-[8px] border-[1px] border-solid border-[#fff] text-center">
            <button onClick={() => onPressConfirmRemove(index)}>
              {!(index === mstRemarks.length - 1) ? <img src={images.icDelete} alt="" className="w-[20px]" /> : null}
            </button>
          </td>
        </tr>
      );
    });
  }, [mstRemarks]);

  const handleChangeRemarks = (value: string, index: number, typeRemarks: string) => {
    const newListMstRemarks = [...mstRemarks];
    newListMstRemarks[index] = { ...newListMstRemarks[index], ...{ [typeRemarks]: value } };
    const errorObject: ErrorMstRemarks = { ...newListMstRemarks[index].error };

    switch (typeRemarks) {
      case 'mrm_remarks_cd':
        errorObject.isErrorRemarksCd = false;
        break;
      case 'mrm_remarks':
        errorObject.isErrorRemarks = false;
        break;
    }
    newListMstRemarks[index].error = { ...newListMstRemarks[index].error, ...errorObject };
    if (newListMstRemarks[index].isBeginData) {
      newListMstRemarks[index].recordState = RecordState.UPDATE;
    }
    setMstRemarks(newListMstRemarks);
  };

  const isEmptyData = (data: MstRemarks) => {
    return data.mrm_remarks_cd === '' && data.mrm_remarks_kb === null && data.mrm_remarks === '';
  };

  const checkError = (itemRemarks: MstRemarks) => {
    const isErrorRemarksCd = itemRemarks.mrm_remarks_cd === '';
    const isErrorRemarks = itemRemarks.mrm_remarks === '';

    return { isErrorRemarksCd, isErrorRemarks };
  };

  const isExistItem = () => {
    const newListRemarks = [...mstRemarks];
    const valueArr = newListRemarks.map(function (item) {
      return item.mrm_remarks_cd.toString();
    });
    const isDuplicate = valueArr.find(function (item, idx) {
      if (valueArr.indexOf(item) !== idx) {
        newListRemarks[idx].error.isErrorRemarksCd = true;
        return true;
      }
      return false;
    });
    setMstRemarks(newListRemarks);
    return isDuplicate;
  };

  const validates = () => {
    const newListRemarks = [...mstRemarks];
    newListRemarks.pop();
    const newListAddError = newListRemarks.map((itemRemarks: MstRemarks) => {
      return { ...itemRemarks, error: checkError(itemRemarks) };
    });
    setMstRemarks(newListAddError);
    const indexItemError = newListRemarks.findIndex(
      (itemRemarks: MstRemarks) => itemRemarks.mrm_remarks_cd === '' || itemRemarks.mrm_remarks === ''
    );
    if (indexItemError !== -1) {
      showAlertMessage('', ErrorMessage.INPUT_ERROR, InfoWord.ERROR, InfoWord.OK);
      return false;
    }
    if (isExistItem()) {
      showAlertMessage('', InfoMessage.REGISTR_DUPLICATION, InfoWord.ERROR, InfoWord.OK);
      return false;
    }
    return true;
  };

  const prepareDataApplyApi = (remarksData: MstRemarks) => {
    return {
      mrm_remarks_cd: remarksData.mrm_remarks_cd,
      mrm_remarks_kb: remarksData.mrm_remarks_kb,
      mrm_remarks: remarksData.mrm_remarks,
    };
  };

  const executeRegister = async () => {
    const mstRemarksNewList = [...mstRemarks];
    const insertData = mstRemarksNewList.filter((item: MstRemarks) => item.recordState === RecordState.INSERT);
    const updateData = mstRemarksNewList.filter((item: MstRemarks) => item.recordState === RecordState.UPDATE);

    // 最終行(新規行)を除外
    insertData.pop();

    const delSet: MutationSet<MstRemarks> = {
      recordState: RecordState.DELETE,
      data: removedMstRemarks,
      func: (data: MstRemarks) => deleteFunc({ variables: { input: { mrm_remarks_cd: data.mrm_remarks_cd } } }),
    };
    const updSet: MutationSet<MstRemarks> = {
      recordState: RecordState.UPDATE,
      data: updateData,
      func: (data: MstRemarks) => updateFunc({ variables: { input: prepareDataApplyApi(data) } }),
    };
    const insSet: MutationSet<MstRemarks> = {
      recordState: RecordState.INSERT,
      data: insertData,
      func: (data: MstRemarks) => insertFunc({ variables: { input: prepareDataApplyApi(data) } }),
    };

    const searchFunc = () => searchMstRemarks({ variables: { input: {} } });

    registerData<MstRemarks>({
      mutationSets1: [delSet, updSet, insSet],
      validates,
      searchFunc,
    });
  };

  const onPressConfirmRegister = () => {
    Auth.currentAuthenticatedUser()
      .then(() =>
        confirmAlert(InfoMessage.CONFIRM_REGISTR, '', InfoWord.WARNING, InfoWord.YES, InfoWord.NO, executeRegister)
      )
      .catch(() => {
        signOut();
        navigate(LOGIN_PATH, { replace: true, state: { from: location } });
      });
  };

  const onPressConfirmRemove = (index: number) => {
    confirmAlert(InfoMessage.CONFIRM_REMOVE, '', InfoWord.WARNING, InfoWord.YES, InfoWord.NO, () =>
      onPressRemove(index)
    );
  };

  const onPressRemove = async (index: number) => {
    const newListRemarks = [...mstRemarks];
    if (index < beginLengthListRemarks) {
      const newListRemove = [...removedMstRemarks];
      const dataItem = mstRemarks[index];
      setBeginLengthListRemarks(beginLengthListRemarks - 1);
      newListRemove.push({ ...dataItem, recordState: RecordState.DELETE });
      setRemovedMstRemarks(newListRemove);
    }
    newListRemarks.splice(index, 1);
    setMstRemarks(newListRemarks);
  };

  useEffect(() => {
    if (mstRemarks?.length < 1) return;
    const lastIndex = mstRemarks.length - 1;
    const newListMstRemarks = [...mstRemarks];

    if (!isEmptyData(newListMstRemarks[lastIndex])) {
      newListMstRemarks.push(emptyRemarks);
      setMstRemarks(newListMstRemarks);
    }
  }, [mstRemarks]);

  useEffect(() => {
    if (!mstRemarksData) return;

    const newListRemarks = mstRemarksData.getMstRemarks.map((item: MstRemarks) => {
      return {
        ...item,
        recordState: RecordState.NONE,
        isCannotEditRemarkCd: true,
        error: defaultErrorMstRemarks,
        isBeginData: true,
      };
    });
    setBeginLengthListRemarks(mstRemarksData.getMstRemarks.length);
    newListRemarks.sort((a: MstRemarks, b: MstRemarks) => Number(a.mrm_remarks_cd) - Number(b.mrm_remarks_cd));
    newListRemarks.push(emptyRemarks);
    setMstRemarks(newListRemarks);
  }, [mstRemarksData]);

  useEffect(() => {
    searchMstRemarks({ variables: { input: {} } });
  }, []);

  return (
    <MainLayout>
      <div className="flex flex-col items-center">
        <p className="p-[20px]">備考管理</p>
        <div className="flex w-4/5">
          <button
            onClick={onPressConfirmRegister}
            className="border-[#000000] border-[1px] p-[10px] mt-[25px] mb-[25px] text-center h-[50px] w-[100px] font-bold"
          >
            登録
          </button>
        </div>
        <table className="border-collapse border-[3px] border-solid border-[#ddd] w-4/5 td:p-[10px]">
          <thead>
            <tr className="hover:opacity-80 even:bg-[#e2efd9]">{renderTableHeader}</tr>
          </thead>
          <tbody>
            {isMstRemarksLoading ? (
              <tr className="hover:opacity-80 even:bg-[#e2efd9]">
                <td
                  colSpan={header.length}
                  className="text-center animate-pulse p-[8px] border-[1px] border-solid border-[#fff]"
                >
                  Loading...
                </td>
              </tr>
            ) : (
              renderTableData
            )}
          </tbody>
        </table>
      </div>
    </MainLayout>
  );
};

export default RemarksManage;
